/**
 * DialogNuevaCita — Modal para crear/editar cita manual desde la agenda.
 *
 * Si recibe `cita` (objeto), edita; si no, crea nueva.
 * Si recibe `fechaSugerida` (Date), pre-llena fecha y hora.
 *
 * Patrón A: sin check-in. Estados: null (por confirmar) → confirmado → completada/cancelada.
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, Slide, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress,
  Autocomplete, Chip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Event as EventIcon,
  Person as PersonIcon,
  DirectionsWalk as WalkIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';

const API_URL = '/api';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#f8fafc', fontSize: 13.5,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5, fontWeight: 600, color: '#475569' },
};

const TIPOS = [
  { id: 'primera_vez',   label: 'Primera vez' },
  { id: 'subsecuente',   label: 'Control / Subsecuente' },
  { id: 'urgencia',      label: 'Urgencia' },
  { id: 'interconsulta', label: 'Interconsulta' },
  { id: 'valoracion',    label: 'Valoración' },
];

const ORIGENES = [
  { id: 'manual',     label: 'Manual',    icon: <EditIcon /> },
  { id: 'telefono',   label: 'Teléfono',  icon: <PhoneIcon /> },
  { id: 'walk_in',    label: 'Walk-in',   icon: <WalkIcon /> },
];

const DURACIONES = [15, 20, 30, 45, 60, 90, 120];

function dateToInputs(d) {
  if (!d) return { fecha: '', hora: '' };
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  const hh = String(dt.getHours()).padStart(2, '0');
  const mi = String(dt.getMinutes()).padStart(2, '0');
  return { fecha: `${yyyy}-${mm}-${dd}`, hora: `${hh}:${mi}` };
}

export default function DialogNuevaCita({
  open, onClose, userId,
  cita = null,           // si viene, modo edición
  fechaSugerida = null,  // si viene, pre-llenar fecha/hora
  onGuardado,
  onEliminado,
  onIniciarConsulta,     // callback opcional: (cita) => void — si está, muestra botón "Iniciar consulta"
}) {
  const esEdicion = Boolean(cita);
  const [pacientes, setPacientes] = useState([]);
  const [pacienteSel, setPacienteSel] = useState(null);
  const [pacienteText, setPacienteText] = useState('');
  const [telefono, setTelefono] = useState('');
  const [correo, setCorreo] = useState('');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [duracion, setDuracion] = useState(30);
  const [tipo, setTipo] = useState('primera_vez');
  const [origen, setOrigen] = useState('manual');
  const [motivo, setMotivo] = useState('');
  const [notas, setNotas] = useState('');
  const [estado, setEstado] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [conflictos, setConflictos] = useState(null); // { mensaje, conflictos: [{id, paciente_nombre, fecha_cita, duracion_minutos}], buffer_min }

  // Cargar pacientes para autocomplete
  useEffect(() => {
    if (!open || !userId) return;
    fetch(`${API_URL}/emr/pacientes?limit=200`, { headers: { 'X-User-Id': userId } })
      .then(r => r.ok ? r.json() : { pacientes: [] })
      .then(d => setPacientes(d.pacientes || []))
      .catch(console.error);
  }, [open, userId]);

  // Sincronizar campos al abrir
  useEffect(() => {
    if (!open) return;
    setError('');
    if (cita) {
      const { fecha, hora } = dateToInputs(cita.fecha_cita);
      setPacienteText(cita.paciente_nombre || '');
      setPacienteSel(null);
      setTelefono(cita.numero_whatsapp || '');
      setCorreo(cita.correo || '');
      setFecha(fecha);
      setHora(hora);
      setDuracion(cita.duracion_minutos || 30);
      setTipo(cita.tipo_consulta || 'primera_vez');
      setOrigen(cita.origen || 'manual');
      setMotivo(cita.motivo || '');
      setNotas(cita.notas || '');
      setEstado(cita.estado);
    } else {
      const sug = fechaSugerida ? dateToInputs(fechaSugerida) : { fecha: '', hora: '' };
      const hoy = new Date();
      setPacienteText('');
      setPacienteSel(null);
      setTelefono('');
      setCorreo('');
      setFecha(sug.fecha || dateToInputs(hoy).fecha);
      setHora(sug.hora || '09:00');
      setDuracion(30);
      setTipo('primera_vez');
      setOrigen('manual');
      setMotivo('');
      setNotas('');
      setEstado(null);
    }
  }, [open, cita, fechaSugerida]);

  // Pre-llenar datos cuando se selecciona un paciente del autocomplete
  const handleSelectPaciente = (p) => {
    setPacienteSel(p);
    if (p) {
      setPacienteText(p.nombre_completo);
      if (p.telefono) setTelefono(p.telefono);
      if (p.correo_electronico) setCorreo(p.correo_electronico);
    }
  };

  const fechaCitaISO = useMemo(() => {
    if (!fecha || !hora) return null;
    return new Date(`${fecha}T${hora}:00`).toISOString();
  }, [fecha, hora]);

  const valido = pacienteText.trim().length >= 2 && fecha && hora && duracion > 0;

  const handleGuardar = async (forzar = false) => {
    if (!valido) return;
    setGuardando(true);
    setError('');
    if (!forzar) setConflictos(null);
    try {
      const body = {
        paciente_nombre: pacienteText.trim(),
        numero_whatsapp: telefono.trim() || '',
        correo: correo.trim() || null,
        fecha_cita: fechaCitaISO,
        duracion_minutos: Number(duracion),
        tipo_consulta: tipo,
        motivo: motivo.trim() || null,
        notas: notas.trim() || null,
        origen,
      };
      const baseUrl = esEdicion ? `${API_URL}/emr/citas/${cita.id}` : `${API_URL}/emr/citas`;
      const url = forzar ? `${baseUrl}?forzar=true` : baseUrl;
      const method = esEdicion ? 'PUT' : 'POST';
      // En edición, también permitir cambio de estado
      if (esEdicion && estado !== undefined) {
        body.estado = estado || '';
      }
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(body),
      });
      if (res.status === 409) {
        // Conflicto de horario — pedir confirmación
        const data = await res.json();
        setConflictos(data.detail || { mensaje: 'Conflicto', conflictos: [] });
        setGuardando(false);
        return;
      }
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt.substring(0, 250));
      }
      onGuardado?.();
    } catch (e) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async () => {
    if (!cita?.id) return;
    if (!window.confirm(`¿Eliminar la cita de ${cita.paciente_nombre}?`)) return;
    setGuardando(true);
    try {
      // Marcamos como cancelado vía PUT (no hay DELETE explícito)
      const res = await fetch(`${API_URL}/emr/citas/${cita.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ estado: 'cancelado' }),
      });
      if (!res.ok) throw new Error(await res.text());
      onEliminado?.();
    } catch (e) {
      setError('No se pudo cancelar: ' + e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !guardando && onClose()}
      TransitionComponent={SlideUp}
      maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3, background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 60px rgba(15,23,42,0.25)', overflow: 'hidden',
        },
      }}
    >
      {/* Header slim */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        py: 1, pl: 2, pr: 1,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)', color: '#fff',
      }}>
        <EventIcon sx={{ fontSize: 18, color: '#fbbf24' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.2, lineHeight: 1.1 }}>
            {esEdicion ? 'Editar cita' : 'Nueva cita'}
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.8 }}>
            {esEdicion ? `Cita #${cita.id}` : 'Crear cita manual desde la agenda'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={guardando}
          sx={{ color: 'rgba(255,255,255,0.85)', p: 0.4 }}>
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '74vh', overflowY: 'auto' }}>

        {/* Paciente — autocomplete o texto libre */}
        <Autocomplete
          freeSolo size="small" sx={inputStyle}
          options={pacientes}
          getOptionLabel={(o) => typeof o === 'string' ? o : (o.nombre_completo || '')}
          value={pacienteSel}
          inputValue={pacienteText}
          onInputChange={(_, v) => setPacienteText(v)}
          onChange={(_, v) => {
            if (typeof v === 'string') {
              setPacienteText(v);
              setPacienteSel(null);
            } else if (v) {
              handleSelectPaciente(v);
            } else {
              setPacienteSel(null);
            }
          }}
          renderInput={(p) => <TextField {...p} label="Paciente" placeholder="Buscar registrado o escribir nombre..." required />}
          renderOption={(props, opt) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: 1, background: '#eff6ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#1e3a5f', fontSize: 12, fontWeight: 800,
                }}>
                  {(opt.nombre_completo || '?').charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{opt.nombre_completo}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                    {opt.telefono || 'sin teléfono'}{opt.numero_expediente ? ` · ${opt.numero_expediente}` : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        />

        {/* Teléfono + correo */}
        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            label="Teléfono / WhatsApp"
            placeholder="+5215512345678"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
          />
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            type="email" label="Correo (opcional)"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
          />
        </Box>

        {/* Fecha + hora + duración */}
        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            type="date" label="Fecha" required
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            sx={{ ...inputStyle, flex: 0.8 }} size="small"
            type="time" label="Hora" required
            value={hora}
            onChange={(e) => setHora(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ ...inputStyle, minWidth: 110 }}>
            <InputLabel shrink>Duración</InputLabel>
            <Select label="Duración" notched value={duracion} onChange={(e) => setDuracion(e.target.value)}>
              {DURACIONES.map(d => (
                <MenuItem key={d} value={d}>{d} min</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Tipo + origen */}
        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <FormControl size="small" sx={{ ...inputStyle, flex: 1 }}>
            <InputLabel shrink>Tipo de consulta</InputLabel>
            <Select label="Tipo de consulta" notched value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {TIPOS.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {/* Origen */}
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.6 }}>
            Origen de la cita
          </Typography>
          <ToggleButtonGroup
            value={origen} exclusive size="small"
            onChange={(_, v) => v && setOrigen(v)}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}
          >
            {ORIGENES.map(o => (
              <ToggleButton
                key={o.id} value={o.id}
                sx={{
                  textTransform: 'none', fontSize: 11.5, fontWeight: 700,
                  border: '1px solid #e2e8f0 !important', borderRadius: '10px !important',
                  px: 1.2, py: 0.5, gap: 0.5, color: '#475569',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                    color: '#fff', borderColor: 'transparent !important',
                    '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
                  },
                }}
              >
                {React.cloneElement(o.icon, { sx: { fontSize: 14 } })}
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Motivo */}
        <TextField
          sx={inputStyle} size="small"
          label="Motivo (opcional)"
          placeholder="Ej: Control de hipertensión"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          inputProps={{ maxLength: 300 }}
        />

        {/* Notas */}
        <TextField
          sx={inputStyle} size="small" multiline minRows={2} maxRows={4}
          label="Notas internas (opcional)"
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
        />

        {/* Estado (solo en edición) */}
        {esEdicion && (
          <FormControl size="small" sx={inputStyle}>
            <InputLabel shrink>Estado</InputLabel>
            <Select label="Estado" notched value={estado || ''} onChange={(e) => setEstado(e.target.value || null)}>
              <MenuItem value="">Por confirmar</MenuItem>
              <MenuItem value="confirmado">Confirmada</MenuItem>
              <MenuItem value="completada">Completada</MenuItem>
              <MenuItem value="cancelado">Cancelada</MenuItem>
              <MenuItem value="no_show">No asistió</MenuItem>
            </Select>
          </FormControl>
        )}

        {error && <Alert severity="error" sx={{ py: 0.4, fontSize: 12 }}>{error}</Alert>}

        {/* Banner de conflicto */}
        {conflictos && (
          <Alert
            severity="warning"
            sx={{
              py: 1, fontSize: 12, alignItems: 'flex-start',
              background: '#fef3c7', border: '1px solid #fde68a',
              '& .MuiAlert-icon': { color: '#b45309' },
            }}
            action={
              <Button size="small" onClick={() => handleGuardar(true)}
                disabled={guardando}
                sx={{
                  textTransform: 'none', fontSize: 11, fontWeight: 700,
                  color: '#b45309', borderRadius: 1.5, whiteSpace: 'nowrap',
                  '&:hover': { background: '#fef9c3' },
                }}
              >
                Agendar igual
              </Button>
            }
          >
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                ⚠ {conflictos.mensaje}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#78350f', mb: 0.5 }}>
                Buffer del médico: {conflictos.buffer_min} min entre citas
              </Typography>
              {(conflictos.conflictos || []).map((c) => {
                const f = new Date(c.fecha_cita);
                return (
                  <Typography key={c.id} sx={{ fontSize: 11, color: '#78350f', fontFamily: 'monospace' }}>
                    • {c.paciente_nombre} · {f.toLocaleDateString('es', { day: '2-digit', month: 'short' })} {f.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })} ({c.duracion_minutos}m)
                  </Typography>
                );
              })}
            </Box>
          </Alert>
        )}
      </Box>

      {/* Acciones */}
      <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
        {esEdicion && cita?.estado !== 'cancelado' && (
          <Button
            onClick={handleEliminar} disabled={guardando}
            startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: 12, color: '#dc2626',
              borderRadius: 2, px: 1.5,
              '&:hover': { background: '#fef2f2' },
            }}
          >
            Cancelar
          </Button>
        )}
        {esEdicion && onIniciarConsulta && cita?.estado !== 'cancelado' && cita?.estado !== 'completada' && (
          <Button
            onClick={() => onIniciarConsulta(cita)} disabled={guardando}
            startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 12, color: '#fff',
              borderRadius: 2, px: 1.5,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
            }}
          >
            Iniciar consulta
          </Button>
        )}
        <Button
          onClick={onClose} disabled={guardando}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: 2, py: 1, px: 2,
            color: '#475569', background: '#f1f5f9',
            '&:hover': { background: '#e2e8f0' },
          }}
        >
          {esEdicion ? 'Cerrar' : 'Cancelar'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={() => handleGuardar(false)} disabled={!valido || guardando}
          startIcon={guardando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: 2, py: 1, px: 3,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            boxShadow: '0 4px 12px rgba(30,58,95,0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
          }}
        >
          {guardando ? 'Guardando...' : (esEdicion ? 'Guardar cambios' : 'Crear cita')}
        </Button>
      </Box>
    </Dialog>
  );
}
